import { create } from 'zustand';
import { api } from '@/lib/api';

interface NearMeState {
  active: boolean;
  radius: number; // km, 0 = any distance
  lat: number | null;
  lng: number | null;
  locating: boolean;
  cityName: string; // reverse-geocoded or user-corrected city name, persisted
  userTown: string; // user-configured "Near Me town" from profile (empty = use GPS)

  // Actions
  toggle: () => void;
  turnOn: () => void;
  turnOff: () => void;
  setRadius: (r: number) => void;
  setPosition: (lat: number, lng: number) => void;
  setCityName: (name: string) => void;
  setUserTown: (town: string) => void;
  loadUserTown: () => Promise<void>;
  requestLocation: () => void;
  activateWithUserTown: () => void;
}

const STORAGE_KEY = 'artrouting-nearme';

interface StoredState { active: boolean; radius: number; lat: number | null; lng: number | null; cityName: string }

function loadFromStorage(): StoredState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        active: !!parsed.active,
        radius: typeof parsed.radius === 'number' && parsed.radius > 0 ? parsed.radius : 50,
        lat: typeof parsed.lat === 'number' ? parsed.lat : null,
        lng: typeof parsed.lng === 'number' ? parsed.lng : null,
        cityName: typeof parsed.cityName === 'string' ? parsed.cityName : '',
      };
    }
  } catch { /* ignore */ }
  return { active: false, radius: 50, lat: null, lng: null, cityName: '' };
}

function saveToStorage(state: StoredState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

const initial = loadFromStorage();

export const useNearMeStore = create<NearMeState>((set, get) => ({
  active: initial.active,
  radius: initial.radius,
  lat: initial.lat,
  lng: initial.lng,
  cityName: initial.cityName,
  userTown: '',
  locating: false,

  toggle: () => {
    const s = get();
    if (s.active) {
      const next = { active: false, radius: s.radius, lat: s.lat, lng: s.lng, cityName: s.cityName };
      saveToStorage(next);
      set({ active: false });
    } else {
      // If user has a configured town, use that instead of GPS
      if (s.userTown) {
        get().activateWithUserTown();
        return;
      }
      if (s.lat !== null && s.lng !== null) {
        const next = { active: true, radius: s.radius, lat: s.lat, lng: s.lng, cityName: s.cityName };
        saveToStorage(next);
        set({ active: true });
      } else {
        get().requestLocation();
      }
    }
  },

  turnOn: () => {
    const s = get();
    if (s.userTown) {
      get().activateWithUserTown();
      return;
    }
    if (s.lat !== null && s.lng !== null) {
      const next = { active: true, radius: s.radius, lat: s.lat, lng: s.lng, cityName: s.cityName };
      saveToStorage(next);
      set({ active: true });
    } else {
      get().requestLocation();
    }
  },

  turnOff: () => {
    const s = get();
    const next = { active: false, radius: s.radius, lat: s.lat, lng: s.lng, cityName: s.cityName };
    saveToStorage(next);
    set({ active: false });
  },

  setRadius: (r: number) => {
    const s = get();
    const next = { active: s.active, radius: r, lat: s.lat, lng: s.lng, cityName: s.cityName };
    saveToStorage(next);
    set({ radius: r });
  },

  setPosition: (lat: number, lng: number) => {
    const s = get();
    const next = { active: s.active, radius: s.radius, lat, lng, cityName: s.cityName };
    saveToStorage(next);
    set({ lat, lng });
  },

  setCityName: (name: string) => {
    const s = get();
    const next = { active: s.active, radius: s.radius, lat: s.lat, lng: s.lng, cityName: name };
    saveToStorage(next);
    set({ cityName: name });
  },

  setUserTown: (town: string) => {
    set({ userTown: town });
  },

  loadUserTown: async () => {
    try {
      const data = await api.get<{ nearme_town: string }>('/auth/nearme-town');
      set({ userTown: data.nearme_town || '' });
    } catch {
      // Not logged in or endpoint not available — ignore
    }
  },

  requestLocation: () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    set({ locating: true });
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const s = get();
        // Clear cityName when getting fresh GPS — will be re-geocoded by FilterAccordion
        const next = { active: true, radius: s.radius, lat, lng, cityName: '' };
        saveToStorage(next);
        set({ lat, lng, active: true, locating: false, cityName: '' });
      },
      (err) => {
        set({ locating: false });
        alert('Could not get your location: ' + err.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  },

  // Internal: activate Near Me using the user's configured town
  activateWithUserTown: () => {
    const s = get();
    set({ locating: true });
    const town = s.userTown;

    // Forward-geocode the user's town to get coordinates
    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(town)}&format=json&limit=1`, {
      headers: { 'User-Agent': 'ArtRouting/1.0 (artrouting.com)', 'Accept': 'application/json' },
    })
      .then((r) => r.json())
      .then((results: Array<{ lat: string; lon: string }>) => {
        if (results.length > 0 && results[0].lat && results[0].lon) {
          const lat = parseFloat(results[0].lat);
          const lng = parseFloat(results[0].lon);
          const next = { active: true, radius: s.radius, lat, lng, cityName: town };
          saveToStorage(next);
          set({ lat, lng, active: true, locating: false, cityName: town });
        } else {
          // Geocoding failed — fall back to GPS
          set({ locating: false });
          get().requestLocation();
        }
      })
      .catch(() => {
        set({ locating: false });
        get().requestLocation();
      });
  },
}));

// Haversine distance helper - exported for use by pages
export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function formatDistance(km: number): string {
  if (km < 1) return Math.round(km * 1000) + ' m';
  if (km < 10) return km.toFixed(1) + ' km';
  return Math.round(km) + ' km';
}

export const RADIUS_OPTIONS = [
  { value: 2, label: '2 km' },
  { value: 5, label: '5 km' },
  { value: 10, label: '10 km' },
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
  { value: 100, label: '100 km' },
];
